/*
 * CUSTOM: Complementary products color sync
 * Swaps each complementary ("Pairs well with") card's main image to the variant
 * image whose color matches the main product's currently selected color.
 *
 * The <product-recommendations> component loads its cards without a variant/color
 * context, so the match cannot be resolved server-side. Each card is wrapped in a
 * <custom-complementary-color> element carrying a { colorHandle: { img, vid } } map;
 * this controller reads the main product color (initial state + on `variant:change` /
 * `product:rerender`) and applies the matching image AND points the card links to the
 * matching variant (reverting both to the server defaults when nothing matches).
 */
(() => {
  const IMAGE_SELECTORS = '.horizontal-product-card__image, .product-card__image--primary';
  const MAIN_FORM_PREFIX = 'product-form-main-';

  // Lightweight handleize for the simple ASCII color handles used here.
  const handleize = (value) =>
    String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  const colorFromVariant = (variant, position) => {
    if (!variant || !position || !Array.isArray(variant.options)) return '';
    return handleize(variant.options[position - 1]);
  };

  const readMainColor = (position) => {
    if (!position) return '';
    const script = document.querySelector('variant-picker[context="main_product"] script[data-variant]');
    if (!script) return '';
    try {
      return colorFromVariant(JSON.parse(script.textContent), position);
    } catch (error) {
      return '';
    }
  };

  class CustomComplementaryColor extends HTMLElement {
    connectedCallback() {
      CustomComplementaryColor.instances.add(this);
      CustomComplementaryColor.ensureListener();

      try {
        this.colorMedia = JSON.parse(this.dataset.colorMedia || '{}');
      } catch (error) {
        this.colorMedia = {};
      }
      this.position = parseInt(this.dataset.mainColorPosition, 10) || 0;
      this.productUrl = this.dataset.productUrl || '';

      this.apply(readMainColor(this.position));
    }

    disconnectedCallback() {
      CustomComplementaryColor.instances.delete(this);
    }

    apply(mainHandle) {
      const img = this.querySelector(IMAGE_SELECTORS);
      if (!img) return;

      // Remember the server-rendered defaults (image + product links) so we can
      // revert when the newly selected main color no longer matches this card.
      if (this.defaultSrc === undefined) {
        this.defaultSrc = img.getAttribute('src');
        this.defaultSrcset = img.getAttribute('srcset');
        this.links = Array.from(this.querySelectorAll('a[href]')).map((a) => ({
          a,
          href: a.getAttribute('href'),
        }));
      }

      const entry = this.matchEntry(mainHandle);

      if (entry && entry.img) {
        if (img.getAttribute('src') !== entry.img) {
          // Drop the responsive srcset so the browser uses the matched-color src.
          img.removeAttribute('srcset');
          img.setAttribute('src', entry.img);
        }
        if (entry.vid && this.productUrl) {
          const variantHref = `${this.productUrl}?variant=${entry.vid}`;
          this.links.forEach(({ a }) => a.setAttribute('href', variantHref));
        }
      } else {
        // No match -> restore the default image and links.
        if (this.defaultSrc != null && img.getAttribute('src') !== this.defaultSrc) {
          if (this.defaultSrcset != null) {
            img.setAttribute('srcset', this.defaultSrcset);
          }
          img.setAttribute('src', this.defaultSrc);
        }
        this.links.forEach(({ a, href }) => {
          if (href != null) a.setAttribute('href', href);
        });
      }
    }

    matchEntry(mainHandle) {
      if (!mainHandle || !this.colorMedia) return null;

      const keys = Object.keys(this.colorMedia);
      if (!keys.length) return null;

      // Match on hyphen segments so a color contained in the main color matches
      // regardless of position, e.g. main "coast-desert" matches "desert" and
      // "coast", and "poppy-macaron" matches "poppy". Priority: exact handle,
      // then the recommended color sharing the most segments with the main color.
      const mainSegments = mainHandle.split('-').filter(Boolean);
      let match = '';

      if (keys.indexOf(mainHandle) !== -1) {
        match = mainHandle;
      } else {
        let bestScore = 0;
        keys.forEach((key) => {
          const overlap = key.split('-').filter((segment) => mainSegments.indexOf(segment) !== -1).length;
          if (overlap > bestScore) {
            bestScore = overlap;
            match = key;
          }
        });
      }

      return match ? this.colorMedia[match] || null : null;
    }

    static ensureListener() {
      if (CustomComplementaryColor.listening) return;
      CustomComplementaryColor.listening = true;

      // On a partial rerender the complementary block is NOT re-rendered (its block
      // type is not in product-rerender's swap list), so existing cards must be
      // updated here. We re-read the main color from the (freshly rerendered)
      // variant-picker rather than the event payload, and wait a frame so the
      // picker swap has settled.
      const reapply = () => {
        requestAnimationFrame(() => {
          CustomComplementaryColor.instances.forEach((instance) => {
            instance.apply(readMainColor(instance.position));
          });
        });
      };

      // `variant:change` bubbles from the main product form to document while the
      // form is still attached, so a document listener catches it reliably.
      document.addEventListener('variant:change', (event) => {
        const target = event.target;
        // Only react to the main product form, not card quick-buy modals.
        // Read the id via getAttribute: on the product form `target.id` can resolve to
        // the form's `<input name="id">` control (DOM named-property shadowing) instead
        // of the id string — an element has no `.indexOf`, which threw when the size
        // finder preselects a variant programmatically. getAttribute returns the string.
        const targetId = target && target.getAttribute ? target.getAttribute('id') : null;
        if (!targetId || targetId.indexOf(MAIN_FORM_PREFIX) !== 0) return;
        reapply();
      });

      // `product:rerender` does not bubble, so listen on the main form directly as
      // a safety net (the form survives partial rerenders).
      const mainForm = document.querySelector(`form[id^="${MAIN_FORM_PREFIX}"]`);
      if (mainForm) {
        mainForm.addEventListener('product:rerender', reapply);
      }
    }
  }

  CustomComplementaryColor.instances = new Set();
  CustomComplementaryColor.listening = false;

  if (!customElements.get('custom-complementary-color')) {
    customElements.define('custom-complementary-color', CustomComplementaryColor);
  }
})();
